import { FocusEvent } from 'react';
import { FieldHookConfig, useField } from 'formik';

/**
 * A drop-in `useField` that does not mark a still-empty field "touched" the
 * first time it is blurred. Tabbing/clicking through a field without typing
 * into it is not the same as leaving it blank on purpose — the plain
 * `useField` `onBlur` cannot tell those apart, so an empty required field
 * flagged "required" the instant you glanced at it and moved on.
 *
 * Once a field has a value, or has already been touched (e.g. Continue/Submit
 * force-touched every field to reveal what is missing), blur validates
 * normally.
 */
export function useGentleBlurField<Value = string>(
  nameOrConfig: string | FieldHookConfig<Value>
) {
  const [field, meta, helpers] = useField(nameOrConfig);

  const isEmpty =
    typeof field.value === 'string' ? field.value.trim() === '' : !field.value;

  const onBlur = (event: FocusEvent<HTMLElement>) => {
    if (isEmpty && !meta.touched) {
      return;
    }

    field.onBlur(event);
  };

  return [{ ...field, onBlur }, meta, helpers] as const;
}

export default useGentleBlurField;
